import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, ArrowDown } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.5, 0.78]);

  return (
    <section ref={ref} className="relative h-screen min-h-[720px] overflow-hidden" data-testid="hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/Landscape_of_Darjeeling%2CIndia.jpg/1920px-Landscape_of_Darjeeling%2CIndia.jpg" alt="Hillside homes and tea estates of Darjeeling, North Bengal" className="h-full w-full object-cover" />
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-neutral-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY }} className="container-px relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-white/90">
          <span className="h-px w-10 bg-white/70" /> City Head · Content Creator · Since 2020
        </motion.div>

        <h1 className="font-sans text-[13vw] font-extrabold uppercase leading-[0.9] tracking-tight text-white md:text-[10vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>Buy Your First</MaskLine>
          <MaskLine delay={0.3} className="text-white/70">Home The <span className="text-white underline decoration-2 underline-offset-8">Smart</span> Way</MaskLine>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 max-w-xl text-base text-white/80 md:text-lg">
          The smartest starting point for first-time home buyers in India — free calculators, honest guidance, and tools that make you a confident buyer.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/tools">
            <Button className="rounded-none bg-white px-8 py-6 font-mono text-xs uppercase tracking-widest text-black hover:bg-white/85" data-testid="hero-tools-cta">
              Explore the Tools
            </Button>
          </Link>
          <a href="https://www.instagram.com/realestatewithayush/" target="_blank" rel="noreferrer" data-testid="hero-instagram">
            <Button variant="outline" className="rounded-none border-white/40 bg-transparent px-6 py-6 font-mono text-xs uppercase tracking-widest text-white hover:border-white hover:bg-white/10 hover:text-white">
              <Instagram className="mr-2 h-4 w-4" /> Follow · 6k+
            </Button>
          </a>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/60">
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
}
